import React from 'react';
import type { SubjectRangeAnalysis } from 'venn-api';
import type { HistoricalPortfolioNodeAllocationType } from 'venn-utils';
import { StyledLabel, StyledSymbol } from './allocation-item-name-tooltip-shared';
import { formatDateByFrequency } from '../manage-data';
import { Match } from 'effect';

type HistoricalTooltipContentProps = {
  investmentSymbol: string | undefined;
  contentHeader: string;
  rangeAnalysis: Pick<SubjectRangeAnalysis, 'historicalStart' | 'historicalEnd' | 'frequency'>;
  historicalPortfolioNodeAllocationType: HistoricalPortfolioNodeAllocationType | undefined;
};

const getAllocationTypeContent = (
  historicalPortfolioNodeAllocationType: HistoricalPortfolioNodeAllocationType | undefined,
) =>
  Match.value(historicalPortfolioNodeAllocationType).pipe(
    Match.when('DRIFTED', () => (
      <span>
        <sup>1</sup> Allocation is market-adjusted.
      </span>
    )),
    Match.when('FORWARD_FILLED', () => (
      <span>
        <sup>2</sup> Allocation is forward-filled from the last closing value.
      </span>
    )),
    Match.when('USER_UPLOADED', () => null),
    Match.when(undefined, () => null),
    Match.exhaustive,
  );

export const AllocationItemHistoricalTooltipContent = ({
  investmentSymbol,
  contentHeader,
  rangeAnalysis,
  historicalPortfolioNodeAllocationType,
}: HistoricalTooltipContentProps) => {
  const { historicalStart, historicalEnd, frequency } = rangeAnalysis;
  const allocationTypeContent = getAllocationTypeContent(historicalPortfolioNodeAllocationType);
  return (
    <div className="flex flex-col w-[200px] gap-1">
      <div>
        {investmentSymbol ? <StyledSymbol>{investmentSymbol}</StyledSymbol> : null}
        <StyledLabel inverted>{contentHeader}</StyledLabel>
      </div>
      <div>
        {historicalStart && (
          <StyledLabel inverted className="flex flex-row justify-between">
            <span>Analysis Start Date:</span>
            <span>{formatDateByFrequency(historicalStart, frequency)}</span>
          </StyledLabel>
        )}
        {historicalEnd && (
          <StyledLabel inverted className="flex flex-row justify-between">
            <span>Analysis End Date:</span>
            <span>{formatDateByFrequency(historicalEnd, frequency)}</span>
          </StyledLabel>
        )}
        {allocationTypeContent && <StyledLabel inverted>{allocationTypeContent}</StyledLabel>}
      </div>
    </div>
  );
};
